'use client';

import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Barcode, 
  HelpCircle, 
  Check, 
  Image as ImageIcon,
  AlertCircle
} from 'lucide-react';
import { ProductItem } from './ProductsCategoryList';

interface ProductFormViewProps {
  initialProduct?: ProductItem | null;
  categories: string[];
  onBack: () => void;
  onSave: (formData: any) => Promise<void>;
}

export default function ProductFormView({
  initialProduct,
  categories,
  onBack,
  onSave
}: ProductFormViewProps) {
  const [name, setName] = useState(initialProduct?.name || '');
  const [brand, setBrand] = useState(initialProduct?.brand || '');
  const [category, setCategory] = useState(initialProduct?.category || 'Cuidado Capilar');
  const [description, setDescription] = useState(initialProduct?.description || '');
  const [sku, setSku] = useState(initialProduct?.sku || '');
  const [barcode, setBarcode] = useState(initialProduct?.barcode || '');
  const [price, setPrice] = useState(initialProduct?.salePrice?.toString() || '');
  const [cost, setCost] = useState(initialProduct?.costPrice?.toString() || '');
  const [commission, setCommission] = useState(initialProduct?.commissionRate?.toString() || '0');
  const [quantity, setQuantity] = useState('100');
  const [unit, setUnit] = useState('gr');
  const [stock, setStock] = useState(initialProduct?.stock?.toString() || '10');
  const [controlStock, setControlStock] = useState(true);
  const [imageUrl, setImageUrl] = useState(initialProduct?.imageUrl || '');
  
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const isEditing = Boolean(initialProduct);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErrorMsg('El nombre del producto es obligatorio.');
      return;
    }
    if (!price || Number(price) <= 0) {
      setErrorMsg('El precio de venta debe ser mayor a cero.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      await onSave({
        id: initialProduct?.id,
        name: name.trim(),
        brand: brand.trim(),
        category: category.trim() || 'General',
        description: description.trim(),
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
        salePrice: Number(price),
        costPrice: Number(cost || 0),
        commissionRate: Number(commission || 0),
        stock: controlStock ? Number(stock || 0) : 9999,
        imageUrl: imageUrl.trim() || null,
        active: true
      });
      onBack();
    } catch (err: any) {
      setErrorMsg(err.message || 'Error al guardar el producto.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl pb-16">
      {/* Botón de retroceso y título */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="p-2 rounded-xl bg-white border border-slate-200/90 hover:bg-slate-50 text-slate-700 shadow-sm transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
          {isEditing ? 'Editar producto' : 'Nuevo producto'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* 1. Tarjeta Identificación */}
        <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
            Identificación
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Nombre <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Crema hidratante"
                className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Marca
              </label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="La Roche-Posay"
                className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Categoría
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition cursor-pointer"
            >
              <option value="Sin categoría">Sin categoría</option>
              <option value="Cuidado Capilar">Cuidado Capilar</option>
              <option value="Barba y Afeitado">Barba y Afeitado</option>
              <option value="Facial y Piel">Facial y Piel</option>
              <option value="Accesorios">Accesorios</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Descripción
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción del producto..."
              className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl p-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
            />
          </div>
        </div>

        {/* 2. Tarjeta Códigos */}
        <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
            Códigos
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-bold text-slate-700">SKU</label>
                <span className="text-[11px] text-slate-400">Código interno</span>
              </div>
              <input
                type="text"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="CRM-001"
                className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm font-mono text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-bold text-slate-700">Código de barras</label>
                <span className="text-[11px] text-slate-400">EAN / UPC — escaneá con cámara o escáner USB</span>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  placeholder="7790000000000"
                  className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl pl-3.5 pr-10 py-2.5 text-sm font-mono text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                />
                <Barcode className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400">
            Tu dispositivo no soporta lectura por cámara. Conectá un escáner USB y enfocá el campo — funciona como teclado.
          </p>
        </div>

        {/* 3. Tarjeta Precio y Costo */}
        <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
            Precio y costo
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Precio (ARS) <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                required
                min={0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm font-bold text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-bold text-slate-700">Costo (ARS)</label>
                <span className="text-[11px] text-slate-400">Para calcular margen</span>
              </div>
              <input
                type="number"
                min={0}
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="0"
                className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-xs font-bold text-slate-700">Comisión (%)</label>
              <span className="text-[11px] text-slate-400">Comisión del profesional por venta</span>
            </div>
            <input
              type="number"
              min={0}
              max={100}
              value={commission}
              onChange={(e) => setCommission(e.target.value)}
              placeholder="0"
              className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
            />
          </div>
        </div>

        {/* 4. Tarjeta Presentación */}
        <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
            Presentación
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-bold text-slate-700">Cantidad</label>
                <span className="text-[11px] text-slate-400">Ej: 250</span>
              </div>
              <input
                type="text"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="250"
                className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Unidad
              </label>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition cursor-pointer"
              >
                <option value="—">—</option>
                <option value="ml">Mililitros (ml)</option>
                <option value="gr">Gramos (gr)</option>
                <option value="un">Unidades (un)</option>
                <option value="oz">Onzas (oz)</option>
                <option value="lt">Litros (lt)</option>
              </select>
            </div>
          </div>
        </div>

        {/* 5. Tarjeta Stock */}
        <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
            Stock
          </h3>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Stock disponible
              </label>
              <input
                type="number"
                min={0}
                value={stock}
                onChange={(e) => setStock(e.target.value)}
                placeholder="0"
                disabled={!controlStock}
                className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm font-bold text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-40 transition"
              />
            </div>

            <div className="pt-5 flex items-center gap-2">
              <input
                type="checkbox"
                id="controlStockCheck"
                checked={controlStock}
                onChange={(e) => setControlStock(e.target.checked)}
                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
              />
              <label htmlFor="controlStockCheck" className="text-xs font-bold text-slate-800 cursor-pointer">
                Controlar stock
              </label>
            </div>
          </div>
        </div>

        {/* 6. Tarjeta Imágenes */}
        <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
            Imágenes
          </h3>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              URL de imagen de demostración o catálogo
            </label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://images.unsplash.com/photo-..."
              className="w-full bg-slate-50/70 border border-slate-200 rounded-2xl px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((idx) => (
              <div 
                key={idx}
                className="h-28 rounded-2xl border-2 border-dashed border-slate-200 hover:border-slate-300 bg-slate-50 flex items-center justify-center text-slate-400 transition"
              >
                {idx === 1 && imageUrl ? (
                  <img src={imageUrl} alt="preview" className="h-full w-full object-cover rounded-xl" />
                ) : (
                  <ImageIcon className="w-6 h-6 stroke-[1.2]" />
                )}
              </div>
            ))}
          </div>
        </div>

        {errorMsg && (
          <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-100 text-rose-600 text-xs font-semibold flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Barra de Acciones */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200/80">
          <button
            type="button"
            onClick={onBack}
            className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-[#047857] hover:bg-[#065f46] text-white text-xs font-bold rounded-xl shadow-md transition disabled:opacity-50"
          >
            {submitting ? 'Guardando...' : 'Guardar producto'}
          </button>
        </div>
      </form>
    </div>
  );
}
